import { updateReplaySituationAndEvents, updateStatus } from '@redux/replay/replay.reducer';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from '@services/baseQueryWithReAuth';
import { EventSourcePolyfill } from 'event-source-polyfill';

import { ReplayStreamCriteria } from '@/types/replay/replay.types';

export const REPLAY_URL = import.meta.env.VITE_REPLAY_URL;

export const replayApi = createApi({
  reducerPath: 'replayApi',
  baseQuery: baseQueryWithReAuth,
  keepUnusedDataFor: 0,
  endpoints: (builder) => ({
    getReplayStream: builder.query({
      queryFn: async () => ({
        data: null,
      }),
      onCacheEntryAdded: async ({ site, token, time }, { dispatch, cacheDataLoaded, cacheEntryRemoved }) => {
        const eventSource = new EventSourcePolyfill(`${REPLAY_URL}/situation/stream?site=${site}&time=${time}`, {
          headers: {
            Authorization: token,
            'Cache-Control': 'no-cache',
            Connection: 'keep-alive',
            'Content-Type': 'text/event-stream',
          },
        });
        const abortController = new AbortController();

        try {
          await cacheDataLoaded;
          eventSource.addEventListener('message', (ev: { data: string; lastEventId: string }) => {
            if (ev.lastEventId !== 'HEARTBEAT') {
              const parsedData = JSON.parse(ev.data);
              if (parsedData.type === 'STATUS') {
                dispatch(updateStatus(parsedData.data));
              } else {
                dispatch(
                  updateReplaySituationAndEvents({
                    situation: JSON.parse(parsedData.data), // Yes it is parsed twice on purpose
                    events: parsedData.replaySituationEvents,
                  }),
                );
              }
            }
          });
          eventSource.addEventListener('error', (e) => {
            console.error('Error: ', e);
          });
          window.addEventListener(
            'beforeunload',
            () => {
              eventSource.close();
            },
            { signal: abortController.signal },
          );
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`
        }
        await cacheEntryRemoved;
        abortController.abort();
        eventSource.close();
      },
    }),
    updateStream: builder.mutation<void, ReplayStreamCriteria>({
      query: ({ uuid, command }) => ({
        url: `${REPLAY_URL}/situation/stream/${uuid}`,
        method: 'PUT',
        body: command,
      }),
    }),
    getAvailableSituationRanges: builder.query<
      { from: string; to: string }[],
      { site: string; from: string; to: string }
    >({
      query: ({ site, from, to }) => ({
        url: `${REPLAY_URL}/situation/available-situation-ranges`,
        method: 'GET',
        params: {
          site,
          from,
          to,
        },
      }),
    }),
  }),
});

export const { useGetReplayStreamQuery, useUpdateStreamMutation, useGetAvailableSituationRangesQuery } = replayApi;
